import { useEffect, useRef } from 'react'

const BOTTOM_BAR_HEIGHT_VAR = '--mc-bottom-bar-h'

// `.mc-sticky--bottom` bars are position:fixed and reserve no space in flow, so
// whatever ends the page sits underneath them. The page reserves the clearance
// from this property (see the `.mc-page` rule in `pages/base.scss`) — never
// hardcode it, since the bar's height is intrinsic and grows with promo copy and
// on narrower screens.
export const useReserveBottomBarSpace = <
  T extends HTMLElement = HTMLDivElement,
>() => {
  const ref = useRef<T>(null)

  useEffect(() => {
    const bar = ref.current
    // The bar is position:fixed but still a DOM descendant of the wrapper.
    const page = bar?.closest<HTMLElement>('.mc-page')
    if (!bar || !page) return undefined

    const publish = () =>
      page.style.setProperty(
        BOTTOM_BAR_HEIGHT_VAR,
        `${bar.getBoundingClientRect().height}px`,
      )

    // ResizeObserver fires no initial callback for an element with no layout box
    // (a bar inside a `d-md-none` wrapper, or a bar that is `display: contents`,
    // at >=768px), so publish once here. Crossing the
    // breakpoint does change the box size (0 <-> H), so the observer covers
    // resizes in both directions without a window listener.
    publish()

    const observer = new ResizeObserver(publish)
    observer.observe(bar)

    return () => {
      observer.disconnect()
      page.style.removeProperty(BOTTOM_BAR_HEIGHT_VAR)
    }
  }, [])

  return ref
}
